import React from 'react';

export type BounceReason = 'Unsubscribed' | 'Hard' | 'Soft' | 'Spam' | 'Blocked';
export const bounceReasonLabel: Record<BounceReason, string> = {
  Unsubscribed: 'Désinscrit',
  Hard: 'Échec permanent',
  Soft: 'Échec temporaire',
  Blocked: 'Bloqué en amont',
  Spam: 'Identifié comme pourriel',
};

export type CallStatus = 'Pending' | 'Success' | 'Failure';
export const callStatusLabel: Record<CallStatus, string> = {
  Pending: 'En cours',
  Success: 'Réussi',
  Failure: 'Échoué',
};

export type CampaignDefaultCallDelay = DefaultCallDelay;

export type CampaignStatus = 'Draft' | 'Active' | 'Archived';
export const campaignStatusLabel: Record<CampaignStatus, string> = {
  Draft: 'Brouillon',
  Active: 'En cours',
  Archived: 'Archivée',
};
export const emailCampaignStatusLabel: Record<CampaignStatus, string> = {
  Draft: 'Brouillon',
  Active: 'Prête à être envoyée',
  Archived: 'Archivée',
};
export const smsCampaignStatusLabel: Record<CampaignStatus, string> = {
  Draft: 'Brouillon',
  Active: 'Prête à être envoyée',
  Archived: 'Archivée',
};

export type CampaignType = 'Call' | 'Email' | 'Sms';
export const campaignTypeLabel: Record<CampaignType, string> = {
  Call: "Campagne d'Appels",
  Email: "Campagne de Courriels",
  Sms: "Campagne de Textos",
};

export type DefaultCallDelay = 'PT1H' | 'P1D' | 'P3D' | 'P1W';
export const defaultcallDelayLabel: Record<DefaultCallDelay, string> = {
  PT1H: '1 heure',
  P1D: '1 jour',
  P3D: '3 jours',
  P1W: '1 semaine',
};

export const emailReplacements: Record<string, keyof Contact> = {
  'Prénom': 'firstName',
  'Nom': 'lastName',
  "Nom du syndicat": 'unionName',
  "Titre d'emploi": 'workTitle',
};

export type Gender = 'Male' | 'Female' | 'Other';
export const genderLabel: Record<Gender, string> = {
  Male: 'Homme',
  Female: 'Femme',
  Other: 'Autre',
};

export type GroupCallStrategy = 'CallEveryoneOnce' | 'CallUndecided' | 'CallProAndVeryPro';
export const groupCallStrategyHint: Record<GroupCallStrategy, React.ReactElement> = {
  CallEveryoneOnce: <div style={{ whiteSpace: 'normal' }}>
    <p>
      Tous les contacts seront ajoutés à la campagne continue. Ils seront appelés
      dans cet ordre:
    </p>
    <ol>
      <li>
        D&apos;abord, les contacts jamais appelés depuis
        le début de cette campagne continue. Un appel sur une autre campagne
        continue ou ponctuelle durant cette campagne compte comme un appel sur
        cette campagne continue.
      </li>
      <li>
        Ensuite, en ordre d&apos;ajout à la campagne (premier ajouté, premier
        appelé) et en commençant par les contacts les moins appelés dans cette
        campagne (tous les contacts seront listés une fois avant la deuxième
        tentative).
      </li>
    </ol>
    <p>
      Lorsqu&apos;un appel permet de déterminer un
      pointage, y compris le pointage «Inconnu», le contact ne sera pas rappelé.
    </p>
    <p>
      <u>C&apos;est la stratégie à choisir lorsqu&apos;on veut faire un
        défrichage de ses listes, préalablement à un ciblage subséquent sur
        d&apos;autres informations et via des des campagnes ponctuelles, de
        courriels ou de textos.</u>
  </p>
  </div>,
  CallUndecided: <div style={{ whiteSpace: 'normal' }}>
    <p>
      Les contacts ayant un pointage «Indécis» seront rappelés en ordre d&apos;ajout à
      la campagne, jusqu&apos;à ce qu&apos;ils soient retirés de la campagne ou que leur
      pointage change.
    </p>
    <p>
      <u>C&apos;est la stratégie à choisir lorsqu&apos;on veut concentrer nos
        efforts sur les contacts ayant montré une ouverture lors d&apos;un
        appel précédent.</u>
    </p>
  </div>,
  CallProAndVeryPro: <div style={{ whiteSpace: 'normal' }}>
    <p>
      Les contacts ayant un pointage «Sûr» ou «Très sûr» seront rappelés en ordre
      d&apos;ajout à la campagne jusqu&apos;à ce qu&apos;ils aient voté ou que
      leur pointage change.
    </p>
    <p>
      <u>C&apos;est la stratégie à choisir pour la sortie de vote.</u>
    </p>
  </div>,
};
export const groupCallStrategyLabel: Record<GroupCallStrategy, string> = {
  CallEveryoneOnce: 'Appeler tout le monde au moins une fois',
  CallUndecided: 'Rappeler les indécis',
  CallProAndVeryPro: "Rappeler les convaincus n'ayant pas encore voté",
};

export type GroupDefaultCallDelay = DefaultCallDelay;

export type Language = 'Fr' | 'En';
export const languageLabel: Record<Language, string> = {
  Fr: 'Français',
  En: 'Anglais',
};

export const pointageResultColor: Record<PointageResult, string> = {
  NoResult: 'grey',
  VeryPro: 'darkgreen',
  Pro: 'green',
  Undecided: 'yellow',
  Con: 'red',
  Unknown: 'black',
};

export type PointageResult = 'NoResult' | 'VeryPro' | 'Pro' | 'Undecided' | 'Con' | 'Unknown';
export const pointageResultLabel: Record<PointageResult, string> = {
  NoResult: 'Jamais pointé',
  VeryPro: 'Très sûr',
  Pro: 'Sûr',
  Undecided: 'Indécis',
  Con: 'Contre',
  Unknown: 'Inconnu',
};

export interface PointageStatsQuery {
  campaignIds?: string;
  groupIds?: string;
}

export interface PointageStats {
  ids: string[];
  result: Record<PointageResult, number>;
  dailyResult: Record<string, Record<PointageResult, number>>;
  calls: Record<CallStatus, number>;
  dailyStatus: Record<string, Record<PointageResult, number>>;
  callsPerCallers: Record<
    string,
    {
      userHumanId: string;
      userName: string;
      callsCount: number;
    }
  >;
}

export type WorkStatus = 'FullTime' | 'PartTime' | 'OnCall';
export const workStatusLabel: Record<WorkStatus, string> = {
  FullTime: 'Temps plein',
  PartTime: 'Temps partiel',
  OnCall: 'Liste de rappel',
};

export interface Model {
  id: string;
  humanId: string;
  sequence: number;
  created: string;
  lastUpdated: string;
}
export type NewModel<T extends Model> = Omit<T, 'id' | 'humanId' | 'sequence' | 'created' | 'lastUpdated'> & Partial<Model>;

export const modelLabels: Record<string, string> = {
  CallCampaign: "Campagne d'appels",
  Group: "Groupe",
};

export interface Call extends Model {
  completed: string;
  contactId: string;
  contact?: Contact;
  campaignId: string;
  campaign?: CallCampaign;
  campaignName: string;
  comment: string;
  groupId: string;
  group?: Group;
  groupName: string;
  leftMessage: boolean;
  result: PointageResult;
  status: CallStatus;
  userId: string;
  user?: User;
  userName: string;
  tags?: Tag[];
}

export interface Campaign extends Model {
  type: CampaignType;

  meta?: Record<string, string | number | string[]> | CallCampaign['meta'];

  contacts?: Contact[];
  contactsCount: number;
  description: string;
  filters?: Record<string, any>;
  groups?: Group[];
  name: string;
  status: CampaignStatus;

  budget?: number;
  usedBudget?: number;

  body?: string;
  emailsCount?: number;
  links?: EmailCampaignLink[];
  replyToEmail: string;
  subject?: string;
  sourceEmailId?: string | null;
  sourceEmail?: SourceEmail | null;

  callsCount?: number;
  canLeaveMessage?: boolean;
  defaultCallDelay?: CampaignDefaultCallDelay;
  maxTries?: number;
  script?: string;
  stats?: CallCampaign['stats'] | EmailCampaign['stats'] | SmsCampaign['stats'];
  calls?: Call[];
  callers?: User[];
  sourcePhoneNumberId?: string | null;
  sourcePhoneNumber?: SourcePhoneNumber | null;

  content?: string;
  smsCount?: number;
  keywords?: SmsCampaignKeyword[];
};

export interface CallCampaign extends AbstractCampaign {
  type: 'Call';

  meta: Partial<{
    canEdit: boolean;
    columns: Array<keyof Contact>;
  }>;

  callsCount?: number;
  canLeaveMessage: boolean;
  defaultCallDelay: CampaignDefaultCallDelay;
  maxTries: number;
  script: string;
  stats?: {
    done: number;
    failed: number;
    pending: number;
    remaining: number;
    succeeded: number;
    total: number;
    new: number;
  };
  calls?: Call[];
  callers?: User[];
}
export function isCallCampaign(item: any): item is CallCampaign {
  return item && item.type === 'Call';
}
export const defaultCampaignColumns: Array<keyof Contact> = [
  'employeeNumber',
  'firstName',
  'lastName',
  'homePhoneNumber',
  'workPhoneNumber',
  'cellPhoneNumber',
  'workTitle',
  'initialUnionName',
  'referrer',
  'region',
  'email',
  'employerEmail',
];

export interface EmailCampaign extends AbstractCampaign {
  type: 'Email';

  meta?: Record<string, string | number | string[]>;

  body: string;
  budget: number;
  emailsCount?: number;
  links: EmailCampaignLink[];
  replyToEmail: string;
  sourceEmailId: string | null;
  sourceEmail?: SourceEmail | null;
  stats?: {
    unsent: number;
    sent: number;
    emails: number;
    bounced: number;
    opened: number;
    clicked: number;
    contacts: number;
  };
  subject: string;
  usedBudget: number;
}
export function isEmailCampaign(item: any): item is EmailCampaign {
  return item && item.type === 'Email';
}

export interface EmailCampaignLink extends Model {
  campaign?: EmailCampaign;
  campaignId: string;
  result: PointageResult | null;
  tag?: Tag | null;
  tagId: string | null;
  redirectUrl: string | null;
  sentCount: number;
}

export interface SmsCampaign extends AbstractCampaign {
  type: 'Sms';

  meta?: Record<string, string | number | string[]>;

  budget: number;
  content: string;
  keywords: SmsCampaignKeyword[];
  smsCount?: number;
  sourcePhoneNumberId?: string | null;
  sourcePhoneNumber?: SourcePhoneNumber | null;
  stats?: {
    unsent: number;
    sent: number;
    sms: number;
    bounced: number;
    answered: number;
    contacts: number;
  };
  usedBudget: number;
}
export function isSmsCampaign(item: any): item is SmsCampaign {
  return item && item.type === 'Sms';
}

export interface SmsCampaignKeyword extends Model {
  campaign?: SmsCampaign;
  campaignId: string;
  result: PointageResult | null;
  tag?: Tag | null;
  tagId: string | null;
  usageCount: number;
  sentCount: number;
  response: string | undefined;
  keyword: string;
}

interface AbstractCampaign extends Model, BaseCampaign {
  type: CampaignType;
}

export interface BaseCampaign {
  contactsCount: number;
  description: string;
  filters?: Record<string, any>;
  name: string;
  status: CampaignStatus;
  contacts?: Contact[];
  groups?: Group[];
}

export interface Contact extends Model {
  address: string | null;
  category: string | null;
  cellPhoneNumber: string | null;
  city: string | null;
  callComments: string;
  comment: string | null;
  email: string | null;
  employeeNumber: string | null;
  employerEmail: string | null;
  employerName: string | null;
  employmentStart: string | null;
  faxPhoneNumber: string | null;
  firstName: string | null;
  gender: Gender | null;
  hasValidEmail: boolean;
  hasValidPhoneNumber: boolean;
  hasVoted: boolean;
  homePhoneNumber: string | null;
  initialUnionName: string | null;
  validCard: string | null;
  language: Language | null;
  lastCalled: string | null;
  lastMembershipStart: string | null;
  lastName: string | null;
  lastResult: PointageResult;
  postalCode: string | null;
  previousAddress: string | null;
  previousMembershipStart: string | null;
  province: string | null;
  referrer: string | null;
  region: string | null;
  service: string | null;
  site: string | null;
  smsAnswers: string | null;
  unionName: string | null;
  unsubscribedFromEmails: boolean;
  unsubscribedFromSms: boolean;
  workDepartment: string | null;
  workPhoneNumber: string | null;
  workShift: string | null;
  workStatus: WorkStatus | null;
  workTitle: string | null;
  workTitleNumber: string | null;
  calls?: Call[];
  campaigns?: Campaign[];
  groups?: Group[];
  tags?: Tag[];
}

export interface ContactTag extends Model {
  contact?: Contact;
  tag?: Tag;
  contactId: string;
  tagId: string;
  createdByUserId: string;
  createdByUser?: User;
}

export interface Email extends Model {
  bounced: string | null;
  campaign?: Campaign;
  campaignId: string;
  clicked: boolean;
  contact?: Contact;
  contactId: string;
  externalId: string;
  lastResult: PointageResult | null;
  opened: boolean;
  sent: string | null;
  toEmail: string;
  tags?: Tag[];
}

export interface EmailTemplate extends Model {
  name: string;
  body: string;
}

export interface Group extends Model {
  name: string;
  description: string;
  callStrategy?: GroupCallStrategy;
  canLeaveMessage: boolean;
  defaultCallDelay?: GroupDefaultCallDelay;
  meta: Partial<{
    canEdit: boolean;
    columns: Array<keyof Contact>;
  }>;
  script: string;
  stats?: {
    total: number;
    done: number;
    remaining: number;
    available: number;
    pending: number;
  };
  contacts?: Contact[];
  contactsCount: number;
  callsCount: number;
  users?: User[];
  userGroups?: UserGroup[];
}

export interface Role extends Model {
  name: string;
  description: string;
  slug: string;
  users?: User[];
  usersCount: number;
  userGroups?: UserGroup[];
}
export function isRole(item: any): item is Role {
  return (
    !!item && typeof item.name === 'string' && typeof item.description === 'string' && typeof item.slug === 'string'
  );
}

export type RoleSlug =
  | 'Admin'
  | 'CallsExport'
  | 'ContactsExport'
  | 'ContactsImport'
  | 'EmailsExport'
  | 'GroupsExport'
  | 'GroupsImport'
  | 'Manager'
  | 'SmsExport'
  | 'UsersExport'
  | 'UsersImport';

export interface SearchResult {
  label: string;
  context: SearchResult[];
  type: 'CallCampaign' | 'Group';
  id: string;
}

export interface Sms extends Model {
  answer: string | null;
  bounced: string | null;
  campaign?: Campaign;
  campaignId: string;
  answered: boolean;
  contact?: Contact;
  contactId: string;
  externalId: string;
  lastResult: PointageResult | null;
  sent: string | null;
  toNumber: string;
  tags?: Tag[];
}

export interface SourceEmail extends Model {
  email: string;
  name: string;
  campaigns?: EmailCampaign[];
}

export interface SourcePhoneNumber extends Model {
  number: string;
  campaigns?: SmsCampaign[];
}

export interface Tag extends Model {
  name: string;
  description?: string;
  contacts?: Contact[];
  contactTags?: ContactTag[];
  contactsCount?: number;
  groupId: string | null;
  group?: Group;
  groupName?: string;
}

export interface User extends Model {
  name: string;
  email: string;
  lastLogin: string | null;
  timezone?: string;
  dateTimeFormat?: string;
  dateFormat?: string;
  phoneNumberFormat?: string;
  roles?: Role[];
  groups?: Group[];
  userGroups?: UserGroup[];
  meta?: {
    documentationPagesSeen?: string[];
  };
}

export interface UserGroup extends Model {
  userId: string;
  user?: User;
  groupId: string;
  group?: Group;
  roles?: Role[];
}

export function isUser(model: any): model is User {
  return !!model && typeof model.name === 'string' && typeof model.email === 'string';
}
